// hooks/useTypeaheadSuggestions.js
//
// The fetch half of a typeahead: debounce a draft, cancel what it supersedes,
// hand back the matches and whether one is in flight. `TypeaheadSearchField`
// is the render half; the two are always used together (every item box through
// features/item-filter's useItemSuggestions, the PO number in PurchaseOrders).
//
// The caller builds the URL, so the request carries whatever scope the page
// is already under — the PO box sends the grid's status / location / category
// filters, and because the URL is a plain string in the effect's deps, a
// change to any of them refetches the open dropdown rather than leaving it
// showing matches from a scope the merchant has left.
//
// A request that fails is REPORTED, not absorbed: `failed` goes back to the
// caller and the field says so, rather than showing the previous term's
// matches or an empty "nothing matched" list.

import { useEffect, useState } from 'react';
import { sessionApi } from '../services/SessionApiService';

// The shortest term worth a request. TypeaheadSearchField reads it too, so the
// box's "type at least N characters" and the fetch agree.
export const TYPEAHEAD_MIN_CHARS = 2;
const DEBOUNCE_MS = 300;

export function useTypeaheadSuggestions({
  // The raw draft text. Trimmed here, once, so the length check and the
  // request agree about what was typed.
  term,
  // Build the request URL for the trimmed term. Called during render; must
  // be pure.
  buildUrl,
  // True while the box shows an already-applied filter verbatim — the user
  // is not searching, so nothing is fetched.
  skip = false,
  // Names the source in the console when a request fails.
  label,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const trimmed = term.trim();
  const url = (!skip && trimmed.length >= TYPEAHEAD_MIN_CHARS) ? buildUrl(trimmed) : null;

  /* eslint-disable react-hooks/set-state-in-effect --
     Async fetch lifecycle: loading + suggestions + abort handling all live
     inside the debounced timer callback. */
  useEffect(() => {
    if (!url) {
      setSuggestions([]);
      setFailed(false);
      return;
    }
    // clearTimeout cancels a pending debounce; abort() cancels an in-flight
    // request whose text is stale so a slow early response can't overwrite
    // a fast later one.
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await sessionApi.get(url, { signal: controller.signal });
        if (!res.ok) throw new Error(`${res.status} from ${url}`);
        const data = await res.json();
        if (!Array.isArray(data.results)) {
          throw new Error(`${url} returned no results array`);
        }
        setSuggestions(data.results);
        setFailed(false);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(`Error fetching ${label} suggestions:`, error);
          setSuggestions([]);
          setFailed(true);
        }
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [url, label]);
  /* eslint-enable react-hooks/set-state-in-effect */

  return { suggestions, loading, failed };
}
